// This eslit disabler is for the event types we can not change
/* eslint-disable no-underscore-dangle */
import React, { type ReactElement } from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import type { EventContentArg } from '@fullcalendar/core';
import moment from 'moment';
import c from 'classnames';
import { t } from 'i18next';

import type { CalendarItem } from '../../../types';
import useRoutes from '../../../hooks/useRoutes';

import './style.scss';

const CalendarItemWithLink: React.FC<{ startDate: Date, innerElement: ReactElement, label: string }>
  = ({ startDate, innerElement, label }) => {
    const routes = useRoutes();
    const registrationLink = `${routes.dailyRegistration}`;
    const search = `?date=${moment(startDate).format('YYYY-MM-DD')}`;
    const location = useLocation();

    return (
      <Link
        aria-label={label}
        to={{
          pathname: registrationLink,
          search,
          state: {
            prevRoute: location.pathname,
          },
        }}
      >
        {innerElement}
      </Link>
    );
  };

const CalendarItemInner: React.FC<EventContentArg> = (props) => {
  const { event } = props;
  const startDate = event._instance?.range.start;
  const registered = event._def.extendedProps.registered as CalendarItem['registered'];
  const success = event._def.extendedProps.success as CalendarItem['success'];
  const failed = event._def.extendedProps.failed as CalendarItem['failed'];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (!startDate) return null;

  // startDate may be set to 01:00:00 by FullCalendar.
  // Make a copy so that no extra events are being made by FullCalendar.
  const compareDate = new Date(startDate.getTime());
  compareDate.setHours(0, 0, 0, 0);

  if (registered) {
    const classes = c('CalendarItem--circle', {
      'CalendarItem--circle__success': success,
      'CalendarItem--circle__failed': failed,
    });
    const label = success ? t('aria_labels.calendar_item_success') : t('aria_labels.calendar_item_fail');

    return (
      <CalendarItemWithLink
        startDate={startDate}
        innerElement={(<div className={classes} />)}
        label={label}
      />
    );
  }
  if (compareDate > today) {
    return <div className="CalendarItem--circle CalendarItem--circle__future" />;
  }
  if (compareDate <= today) {
    return (
      <CalendarItemWithLink
        startDate={startDate}
        innerElement={<i className="icon icon-streak" />}
        label={t('aria_labels.calendar_item_not_registered')}
      />
    );
  }
  return null;
};

const Item: React.FC<EventContentArg> = (props) => (
  <div className="CalendarItem">
    <CalendarItemInner {...props} />
  </div>
);

export default Item;
